import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Lucide from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { cpvApi } from '@/pages/quality/cpv/cpvApi';
import { myActionsApi, totalOf } from '@/pages/quality/cpv/cpvMyActionsApi';
import { CPV_MODULE, CPV_SHEETS_MODULE, holdsSetupGrant, inLiteMenu, menuEntries } from './cpvMenu';
import { useShowAllScreens } from './showAllScreens';
import useAssistantAllowed from '@/pages/quality/cpv/assistant/useAssistantAllowed';
import useReportSchedulesAllowed from '@/pages/quality/cpv/schedules/useReportSchedulesAllowed';
import useValidationPackAllowed from '@/pages/quality/cpv/validation/useValidationPackAllowed';
import useAuditTrailAllowed from '@/pages/quality/cpv/audit/useAuditTrailAllowed';

// ==============================|| MENU ITEMS ||============================== //

const ICON_SIZE = 20;

// The sidebar and the header breadcrumbs both build this menu; they share one read per page.
let countsRead = { path: null, promise: null };
const readCounts = (path) => {
  if (countsRead.path !== path) countsRead = { path, promise: cpvApi.signalCounts() };
  return countsRead.promise;
};

/** The server's open-signal count for this plant (GET /bp/cpv/signals/counts/), re-read on every page change. */
function useOpenSignals(enabled) {
  const location = useLocation();
  const [open, setOpen] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    readCounts(location.pathname)
      .then((c) => alive && setOpen(Number(c?.open) || 0))
      .catch(() => alive && setOpen(0));
    return () => {
      alive = false;
    };
  }, [enabled, location.pathname]);
  return open;
}

// My actions' count: one read per page, shared the same way.
let actionsRead = { path: null, promise: null };
const readActions = (path) => {
  if (actionsRead.path !== path) actionsRead = { path, promise: myActionsApi.list() };
  return actionsRead.promise;
};

/** The CPV acts waiting on the person (the sum of GET /bp/cpv/my-actions/ counts), re-read on every page change. */
function useMyActions(enabled) {
  const location = useLocation();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    readActions(location.pathname)
      .then((d) => alive && setN(totalOf(d?.counts)))
      .catch(() => alive && setN(0));
    return () => {
      alive = false;
    };
  }, [enabled, location.pathname]);
  return n;
}

function Dashboard() {
  const { t } = useTranslation();
  const ac = (key) => Boolean(accessControlUtils(key)?.read_access);
  const anyOf = (...keys) => keys.some((k) => ac(k));
  // Q14: the full menu for the people who set things up, or for anyone who turned on "Show all screens"
  const [showAll] = useShowAllScreens();
  const full = showAll || holdsSetupGrant(anyOf);
  const openSignals = useOpenSignals(ac(CPV_MODULE));
  const myActions = useMyActions(anyOf(CPV_SHEETS_MODULE, CPV_MODULE));
  const assistant = useAssistantAllowed();
  const schedules = useReportSchedulesAllowed();
  const validationPack = useValidationPackAllowed();
  const auditTrail = useAuditTrailAllowed();
  const chips = {
    openSignals: openSignals > 0 ? { label: String(openSignals), color: 'blue' } : null,
    myActions: myActions > 0 ? { label: String(myActions), color: 'orange' } : null,
  };

  const iconOf = (name) => {
    const Icon = Lucide[name] || Lucide.Circle;
    return () => <Icon size={ICON_SIZE} />;
  };
  const toItem = (e) => ({
    id: e.id,
    title: t(e.title),
    type: 'item',
    icon: iconOf(e.icon),
    url: e.url,
    breadcrumbs: true,
    showMenu: Boolean(e.show) && (full || inLiteMenu(e.id)),
    ...(e.chip && chips[e.chip] ? { chip: chips[e.chip] } : {}),
  });
  const toNode = (e) => {
    if (!e.children) return toItem(e);
    const children = e.children.map(toItem);
    return {
      id: e.id,
      title: t(e.title),
      type: 'collapse',
      icon: iconOf(e.icon),
      breadcrumbs: true,
      showMenu: children.some((c) => c.showMenu),
      children,
    };
  };

  return [{ id: 'group-dashboard', type: 'group', children: menuEntries({ ac, anyOf, assistant, schedules, validationPack, auditTrail }).map(toNode) }];
}
export default Dashboard;
